import React from 'react';
import i18n from '../../i18n';
import { IconButton, Tooltip, Box, Typography } from '@mui/material';
import {
  AutoAwesome as AIIcon,
  Check as AcceptIcon,
  Close as RejectIcon,
} from '@mui/icons-material';
import CandidateDropdown from './CandidateDropdown';
import InlineDatePicker from './InlineDatePicker';

// Shared typography styles to match ExperimentsTable
const sharedFontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif';
const sharedLetterSpacing = '-0.01em';

// Helper function to truncate text
const truncateText = (text, maxLength = 27) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

// Format numbers for compact display (e.g., 42100 -> 42.1K)
const formatCompactNumber = (value) => {
  if (!value || isNaN(value)) return value;

  const num = parseFloat(value);

  if (num >= 1000000) {
    return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
  } else if (num >= 1000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  }

  return num.toString();
};

// Get minimal icon for metric type
const getMinimalIcon = (type, icon) => {
  if (type === 'current' && icon === 'arrow_down') return '↓';
  if (type === 'current' && icon === 'half_circle') return '↓';
  if (type === 'benchmark' && icon === 'triangle_up') return '↑';
  if (type === 'market' && icon === 'triangle_up') return '↑';
  // Fallback for old icons
  if (type === 'current') return '↓';
  if (type === 'benchmark') return '↑';
  if (type === 'market') return '↑';
  return '↓';
};

// Get warm color for metric status
const getWarmColor = (status) => {
  switch (status) {
    case 'danger': return '#D32F2F'; // Darker warm red
    case 'warning': return '#F57C00'; // Darker warm amber
    case 'good': return '#388E3C'; // Darker mint green
    case 'normal':
    default: return '#757575'; // Darker warm gray
  }
};

// Create tooltip content for context metrics
/**
 * Tooltip para contextos que NO traen las 3 métricas del detector estacional — hoy, las
 * oportunidades del orquestador: 1 métrica ("Margen bruto incremental"), un `headline` con la
 * recomendación literal y un `actionHint`. Antes estas filas mostraban sólo un guion.
 */
const createSimpleTooltipContent = (context, t) => {
  if (!context) return null;
  const metrics = context.metrics || [];

  return (
    <Box sx={{ padding: '12px', minWidth: '240px', fontFamily: 'SF Pro, Inter, system-ui', fontSize: '11px' }}>
      {context.headline && (
        <Typography sx={{ fontSize: '11px', color: '#2D3748', lineHeight: 1.45, fontWeight: 600, mb: metrics.length ? 1.5 : 0 }}>
          {context.headline}
        </Typography>
      )}

      {metrics.map((m, i) => (
        <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 0.75 }}>
          <Typography sx={{ fontSize: '11px', color: '#718096' }}>{m.label}</Typography>
          <Typography sx={{ fontSize: '11px', fontWeight: 700, color: m.status === 'good' ? '#2e7d32' : '#2D3748' }}>
            {m.status === 'good' ? '+' : ''}{typeof m.value === 'number' ? m.value.toLocaleString(i18n.language) : m.value} {m.unit || ''}
          </Typography>
        </Box>
      ))}

      {(context.insights || []).slice(0, 1).map((ins, i) => (
        <Typography key={i} sx={{ fontSize: '11px', color: '#4A5568', lineHeight: 1.4, mt: 1.25, pt: 1.25, borderTop: '1px solid #E2E8F0' }}>
          {ins.text}
        </Typography>
      ))}

      {context.actionHint && (
        <Typography sx={{ fontSize: '11px', color: '#2e7d32', fontWeight: 600, mt: 1 }}>
          {context.actionHint}
        </Typography>
      )}
    </Box>
  );
};

const createTooltipContent = (metrics, t, oppCost, friendlyDescription) => {
  if (!metrics || metrics.length < 3) return null;

  const [current, benchmark, market] = metrics;
  const hasOppCost = oppCost !== null && oppCost !== undefined && oppCost !== 0;

  return (
    <Box sx={{
      padding: '12px',
      minWidth: '240px',
      fontFamily: 'SF Pro, Inter, system-ui',
      fontSize: '11px'
    }}>
      {/* Problem Type Description - Simple explanation for vending machine operators */}
      {friendlyDescription && (
        <Box sx={{ mb: 1.5, pb: 1.5, borderBottom: '1px solid #E2E8F0' }}>
          <Typography sx={{
            fontSize: '11px',
            color: '#2D3748',
            lineHeight: 1.4,
            fontWeight: 500
          }}>
            {friendlyDescription}
          </Typography>
        </Box>
      )}

      {/* Opportunity Cost Section */}
      {hasOppCost && (
        <Box sx={{ mb: 1.5, pb: 1.5, borderBottom: '1px solid #E2E8F0' }}>
          <Typography sx={{
            fontSize: '12px',
            fontWeight: 600,
            color: '#E57373',
            mb: 0.5,
            letterSpacing: '-0.01em'
          }}>
            €{Math.abs(oppCost).toFixed(0)} {t ? t('lost', { defaultValue: 'perdido' }) : 'perdido'}
          </Typography>
          <Typography sx={{
            fontSize: '10px',
            color: '#718096',
            fontStyle: 'italic',
            lineHeight: 1.3
          }}>
            {t ? t('oppCostCaveat', { defaultValue: 'Projected opportunity cost based on sales performance' }) : 'Projected opportunity cost based on sales performance'}
          </Typography>
        </Box>
      )}

      {/* Title */}
      <Typography sx={{
        fontSize: '12px',
        fontWeight: 600,
        color: '#2D3748',
        mb: 1.5,
        letterSpacing: '-0.01em'
      }}>
        {t ? t('product_performance', { defaultValue: 'Product Performance' }) : 'Product Performance'}
      </Typography>

      {/* Current Sales */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
        <Typography sx={{
          fontSize: '14px',
          color: getWarmColor(current.status),
          mt: -0.2
        }}>
          ↓
        </Typography>
        <Box>
          <Typography sx={{
            fontSize: '11px',
            fontWeight: 500,
            color: '#2D3748',
            lineHeight: 1.2
          }}>
            {current.detail?.description || (t ? t('currentProductSales', { defaultValue: 'Current product sales' }) : 'Current product sales')}
          </Typography>
          <Typography sx={{
            fontSize: '11px',
            color: '#718096',
            lineHeight: 1.2
          }}>
            {formatCompactNumber(current.value)} {current.detail?.description ? '' : (t ? t('units', { defaultValue: 'units' }) : 'units')} {current.detail?.period ? `(${current.detail.period})` : (t ? `(${t('sixWeeks', { defaultValue: '6 weeks' })})` : '(6 weeks)')}
          </Typography>
        </Box>
      </Box>

      {/* Category Benchmark */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
        <Typography sx={{
          fontSize: '14px',
          color: '#388E3C',
          mt: -0.2
        }}>
          ↑
        </Typography>
        <Box>
          <Typography sx={{
            fontSize: '11px',
            fontWeight: 500,
            color: '#2D3748',
            lineHeight: 1.2
          }}>
            {benchmark.detail?.description || (t ? t('categoryAverage', { defaultValue: 'Category average' }) : 'Category average')}
          </Typography>
          <Typography sx={{
            fontSize: '11px',
            color: '#718096',
            lineHeight: 1.2
          }}>
            {formatCompactNumber(benchmark.value)} {benchmark.detail?.description ? '' : (t ? t('unitsIn', { defaultValue: 'units in' }) : 'units in')} {benchmark.detail?.category && !benchmark.detail?.description ? benchmark.detail.category : ''}
          </Typography>
          {benchmark.detail?.sector && (
            <Typography sx={{
              fontSize: '10px',
              color: '#A0AEC0',
              lineHeight: 1.2
            }}>
              {t ? t('sector', { defaultValue: 'Sector' }) : 'Sector'}: {benchmark.detail.sector}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Market Activity */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Typography sx={{
          fontSize: '14px',
          color: '#388E3C',
          mt: -0.2
        }}>
          ↑
        </Typography>
        <Box>
          <Typography sx={{
            fontSize: '11px',
            fontWeight: 500,
            color: '#2D3748',
            lineHeight: 1.2
          }}>
            {market.detail?.description || (t ? t('locationTotalSales', { defaultValue: 'Location total sales' }) : 'Location total sales')}
          </Typography>
          <Typography sx={{
            fontSize: '11px',
            color: '#718096',
            lineHeight: 1.2
          }}>
            {formatCompactNumber(market.value)} {market.detail?.description ? '' : (t ? t('totalSales', { defaultValue: 'total sales' }) : 'total sales')} {market.detail?.period ? `(${market.detail.period})` : (t ? `(${t('sixWeeks', { defaultValue: '6 weeks' })})` : '(6 weeks)')}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const getExpandedRowColumns = ({
  experimentDrafts = {},
  onDraftChange,
  onExperimentUpdate,
  onOpenAIPanel,
  onAcceptExperiment,
  onRejectExperiment,
  navigate,
  t,
  clientId,
}) => {
  const columns = [
    // 1. Machine
    {
      title: t ? t('machine', { defaultValue: 'Machine' }) : 'Machine',
      dataIndex: 'machineAlias',
      key: 'machineId',
      width: 108,
      align: 'center',
      sorter: (a, b) => (a.machineAlias || '').localeCompare(b.machineAlias || '', undefined, { numeric: true }),
      render: (text, record) => {
        const displayText = text || record.machineId;
        const tooltipText = `${record.group || ''} - ${record.location || ''}`;

        const handleClick = () => {
          if (navigate) {
            navigate('/machines', {
              state: {
                defaultTab: 'planograma',
                machineStoreId: record.machineAlias || record.machineId,
                filters: {
                  group: record.group,
                  location: record.location
                }
              }
            });
          }
        };

        return (
          <Tooltip title={tooltipText} arrow placement="top">
            <Typography
              component="span"
              onClick={handleClick}
              sx={{
                color: '#787774',
                textDecoration: 'none',
                cursor: navigate ? 'pointer' : 'default',
                fontSize: '13px',
                fontWeight: 400,
                fontFamily: sharedFontFamily,
                letterSpacing: sharedLetterSpacing,
                '&:hover': navigate ? {
                  color: '#37352f',
                  textDecoration: 'underline'
                } : {}
              }}
            >
              {displayText}
            </Typography>
          </Tooltip>
        );
      },
    },
    // 2. Product → Experiment (combined column)
    {
      title: t ? t('experiment', { defaultValue: 'Experiment' }) : 'Experiment',
      key: 'productExperiment',
      width: 520,
      align: 'center',
      render: (_, record) => {
        const productName = record.productName;
        const needsProductTooltip = productName && productName.length > 18;

        const productContent = (
          <span style={{
            fontSize: '12px',
            fontFamily: sharedFontFamily,
            fontWeight: 400,
            color: '#37352f',
            letterSpacing: sharedLetterSpacing,
            cursor: needsProductTooltip ? 'help' : 'default',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'inline-block',
            textAlign: 'right'
          }}>
            {productName || '-'}
          </span>
        );

        const productElement = needsProductTooltip ? (
          <Tooltip title={productName} arrow placement="top" enterDelay={500}>
            {productContent}
          </Tooltip>
        ) : productContent;

        return (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            flexWrap: 'nowrap'
          }}>
            {/* Product name - fixed width, right-aligned text */}
            <Box sx={{
              width: 290,
              flexShrink: 0,
              display: 'flex',
              justifyContent: 'flex-end',
              overflow: 'hidden'
            }}>
              {productElement}
            </Box>
            {/* Arrow - fixed */}
            <span style={{
              fontSize: '11px',
              color: '#9CA3AF',
              fontFamily: sharedFontFamily,
              flexShrink: 0,
              margin: '0 6px'
            }}>
              →
            </span>
            {/* Dropdown - fixed width */}
            <Box sx={{ width: 208, flexShrink: 0 }}>
              <CandidateDropdown
                experimentId={record.experimentId}
                experimentType={record.experimentType}
                selectedCandidateId={record.selectedCandidateId}
                candidateProductIds={record.candidateProductIds || []}
                candidateNames={record.candidateNames || []}
                candidateDiscounts={record.candidateDiscounts || []}
                discount={record.discount}
                markup={record.markup}
                targetPrice={record.targetPrice}
                onDraftChange={onDraftChange}
                clientId={clientId}
                originalPrice={record.originalPrice}
              />
            </Box>
          </Box>
        );
      },
    },
    // 3. Context with Opp Cost (Contexto)
    {
      title: t ? t('lossContext', { defaultValue: 'Loss & Context' }) : 'Loss & Context',
      dataIndex: 'context',
      key: 'context',
      width: 254,
      align: 'center',
      render: (context, record) => {
        const oppCost = record.opportunityCost;
        const hasOppCost = oppCost !== null && oppCost !== undefined && oppCost !== 0;

        // El backend ya declara el signo: `status: 'good'` = el número es una GANANCIA que se
        // captura actuando (orquestador), no una pérdida ya incurrida. Sin esto todas las filas
        // se pintaban en rojo y con la palabra "perdido", incluidas las subidas de precio.
        const isGain = context?.metrics?.[0]?.status === 'good';
        const amountColor = isGain ? '#2e7d32' : '#E57373';
        const amountWord = isGain
          ? (t ? t('incremental', { defaultValue: 'incremental' }) : 'incremental')
          : (t ? t('lost', { defaultValue: 'perdido' }) : 'perdido');

        const oppCostElement = hasOppCost ? (
          <span style={{
            color: amountColor,
            fontFamily: sharedFontFamily,
            fontWeight: 600,
            fontSize: '12px',
            letterSpacing: sharedLetterSpacing
          }}>
            {isGain ? '+' : ''}€{Math.abs(oppCost).toFixed(0)} <span style={{ fontWeight: 400, fontSize: '11px' }}>{amountWord}</span>
          </span>
        ) : null;

        // El tooltip de 3 métricas es el del detector estacional (`[current, benchmark, market]`).
        // El orquestador manda 1 métrica + headline + actionHint, así que se pinta uno genérico en
        // vez de rellenar métricas falsas para colar la fila por el mismo camino.
        const hasRichContext = context && context.metrics && context.metrics.length >= 3;
        const hasSimpleContext = context && (context.headline || (context.metrics || []).length > 0);

        if (!hasRichContext) {
          const summary = hasSimpleContext ? (
            <Tooltip
              title={createSimpleTooltipContent(context, t)}
              arrow
              placement="top"
              componentsProps={{
                tooltip: {
                  sx: {
                    backgroundColor: '#FFFFFF',
                    color: '#2D3748',
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    maxWidth: 360,
                  }
                }
              }}
            >
              <Typography sx={{
                fontSize: '12px',
                color: '#2D3748',
                fontFamily: sharedFontFamily,
                letterSpacing: sharedLetterSpacing,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: 150,
                cursor: 'help',
              }}>
                {context.headline || context.metrics[0]?.label}
              </Typography>
            </Tooltip>
          ) : (
            <Typography sx={{
              fontSize: '12px',
              color: '#787774',
              fontStyle: 'italic',
              fontFamily: sharedFontFamily,
              letterSpacing: sharedLetterSpacing
            }}>
              -
            </Typography>
          );

          return (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'center' }}>
              {oppCostElement}
              {hasOppCost && (
                <Typography sx={{ fontSize: '10px', color: '#9CA3AF' }}>•</Typography>
              )}
              {summary}
            </Box>
          );
        }

        return (
          <Tooltip
            title={createTooltipContent(context.metrics, t, oppCost, context.friendlyDescription)}
            arrow
            placement="top"
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: '#FFFFFF',
                  color: '#2D3748',
                  border: '1px solid #E2E8F0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  fontSize: '11px',
                  maxWidth: 'none'
                }
              },
              arrow: {
                sx: {
                  color: '#FFFFFF',
                  '&:before': {
                    border: '1px solid #E2E8F0'
                  }
                }
              }
            }}
          >
            <Box sx={{ minWidth: 160, display: 'flex', gap: 1.5, alignItems: 'center', cursor: 'pointer', justifyContent: 'center' }}>
              {oppCostElement}
              {hasOppCost && (
                <Typography sx={{ fontSize: '10px', color: '#9CA3AF' }}>•</Typography>
              )}
              {context.metrics.slice(0, 3).map((metric, index) => {
                // Override colors: current keeps original logic, benchmark and market = green
                let displayColor;
                if (metric.type === 'current') {
                  displayColor = getWarmColor(metric.status);
                } else {
                  displayColor = '#388E3C'; // Darker green for benchmark and market
                }

                const minimalIcon = getMinimalIcon(metric.type, metric.icon);

                return (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.3,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-1px)'
                      }
                    }}
                  >
                    <span style={{
                      color: displayColor,
                      fontSize: '12px',
                      fontFamily: 'SF Pro, Inter, system-ui',
                      letterSpacing: '-0.01em'
                    }}>
                      {minimalIcon}
                    </span>
                    <Typography sx={{
                      fontSize: '12px',
                      color: displayColor,
                      fontWeight: metric.type === 'current' ? 500 : 400,
                      fontFamily: 'SF Pro, Inter, system-ui',
                      letterSpacing: '-0.01em'
                    }}>
                      {formatCompactNumber(metric.value)}
                    </Typography>
                    <Typography sx={{
                      fontSize: '6px',
                      color: '#9E9E9E',
                      fontFamily: 'SF Pro, Inter, system-ui'
                    }}>
                      ●
                    </Typography>
                    <Typography sx={{
                      fontSize: '10px',
                      color: '#9E9E9E',
                      fontWeight: 500,
                      fontFamily: 'SF Pro, Inter, system-ui',
                      letterSpacing: '-0.01em'
                    }}>
                      {metric.label || 'N/A'}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Tooltip>
        );
      },
    },
    // 5. Dates (Fechas)
    {
      title: t ? t('dates', { defaultValue: 'Dates' }) : 'Dates',
      key: 'dates',
      width: 105,
      align: 'center',
      render: (_, record) => (
        <InlineDatePicker
          experimentId={record.experimentId}
          dateFrom={record.dateFrom}
          dateTo={record.dateTo}
          onDraftChange={onDraftChange}
          t={t}
        />
      ),
    },
    // 8. Actions (Acciones)
    {
      title: t ? t('actions', { defaultValue: 'Actions' }) : 'Actions',
      key: 'actions',
      width: 131,
      align: 'center',
      fixed: 'right',
      render: (_, record) => {
        const hasDraft = experimentDrafts?.[record.experimentId]?.hasChanges;

        return (
          <Box display="flex" gap={0.5} alignItems="center" justifyContent="center">
            {/* Unsaved changes indicator */}
            {hasDraft && (
              <Tooltip title={t ? t('unsavedChanges', { defaultValue: 'Unsaved changes' }) : 'Unsaved changes'}>
                <Box sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: '#f59e0b', // Amber dot
                  flexShrink: 0,
                }} />
              </Tooltip>
            )}
            {record.isAI && (
              <Tooltip title={t ? t('aiAnalysis', { defaultValue: 'AI Analysis' }) : 'AI Analysis'}>
                <IconButton
                  size="small"
                  onClick={() => onOpenAIPanel(record)}
                  sx={{
                    background: 'linear-gradient(135deg, #EDE9FE 0%, #DDD6FE 100%)',
                    color: '#7C3AED',
                    width: 24,
                    height: 24,
                    border: '1px solid #C4B5FD',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #DDD6FE 0%, #C4B5FD 100%)',
                    },
                  }}
                >
                  <AIIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={t ? t('accept', { defaultValue: 'Accept' }) : 'Accept'}>
              <IconButton
                size="small"
                onClick={() => onAcceptExperiment(record.experimentId)}
                color="success"
              >
                <AcceptIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={t ? t('reject', { defaultValue: 'Reject' }) : 'Reject'}>
              <IconButton
                size="small"
                onClick={() => onRejectExperiment(record.experimentId)}
                color="error"
              >
                <RejectIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  return columns;
};

export default getExpandedRowColumns;
